/**
 * Response shapes of the shipped `/partner/*` routes, read off
 * server/src/routes/partner/*.ts (the contract's "as shipped" notes win where
 * they differ from its draft tables). The widget config document itself is
 * modelled in config/model.ts, not here.
 */

export const PARTNER_ROLES = ['owner', 'admin', 'developer', 'operator', 'billing'] as const;
export type PartnerRole = (typeof PARTNER_ROLES)[number];

export const PARTNER_TYPES = [
  'property_manager',
  'home_warranty',
  'insurer',
  'contractor_network',
  'trade_company',
  'membership',
  'media',
  'retailer',
  'other',
] as const;
export type PartnerType = (typeof PARTNER_TYPES)[number];

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

// ── Auth ────────────────────────────────────────────────────────────────────
export interface CodeResponse {
  challenge_id: string;
  expires_in_s: number;
}
/** `GET /partner/auth/account`: the signed-in person, not an organisation. */
export interface Account {
  partner_user_id: number;
  email: string;
  /** A Google account is linked (by its id, migration 073), so "Continue with Google" signs in here. */
  google_linked: boolean;
  /** The server has Google sign-in configured at all. */
  google_signin_available: boolean;
  /** This session proved the current mailbox in the last 10 minutes (step-up, migration 086). */
  step_up_fresh?: boolean;
  /** The linked Google account's address (migration 090, display only); null for an older link. */
  google_email?: string | null;
  /** Hand to Google as the OIDC nonce when linking a Google account from this session. */
  google_link_nonce?: string | null;
}
/** `POST /partner/auth/google/link`. */
export interface GoogleLinked {
  google_linked: true;
  google_email: string | null;
}
/** `POST /partner/auth/google-link-revert/preview`: what the "This wasn't me" link would undo. */
export interface GoogleLinkRevertPreview {
  account_email: string;
  /** Masked (`k***@gmail.com`), or null when Google gave no address. */
  google_email: string | null;
  linked_at: string;
  expires_at: string;
  used: boolean;
}
/** `POST /partner/auth/google-link-revert`. */
export interface GoogleLinkReverted {
  email: string;
  google_unlinked: boolean;
  sessions_signed_out: number;
  agent_tokens_revoked: number;
}
/** `POST /partner/auth/step-up/verify`. */
export interface StepUpVerified {
  step_up_valid_for_s: number;
}
/** `POST /partner/auth/email-revert/preview`: what the "This wasn't me" link would undo. */
export interface EmailRevertPreview {
  /** The address the link puts back (the mailbox that received it). */
  restores_email: string;
  /** The address it was changed to, masked (`n***@domain`). */
  changed_to: string;
  changed_at: string;
  expires_at: string;
  used: boolean;
}
/** `POST /partner/auth/email-revert`. */
export interface EmailReverted {
  email: string;
  sessions_signed_out: number;
  agent_tokens_revoked: number;
  google_unlinked: boolean;
}
/** Migration 093: a person's choice for AI agent notices (Your account). */
export type NoticeMode = 'all' | 'digest' | 'security';
/** `GET /partner/auth/account/notifications`. */
export interface NoticePrefs {
  mode: NoticeMode;
  /** The person is an owner or admin somewhere, so the choice does something. */
  applies: boolean;
  security_always_on: string[];
}
/** An extra address for AI agent and security notices (`/partner/org/notice-recipients`). */
export interface NoticeRecipient {
  id: string;
  email: string;
  status: 'pending' | 'confirmed';
  added_by_email: string | null;
  created_at: string;
  confirmed_at: string | null;
  confirm_expires_at: string | null;
  /** On add and resend: whether the confirmation email went out. */
  confirmation_sent?: boolean;
}
/** `POST /partner/auth/email-change/verify`. */
export interface EmailChanged {
  email: string;
  google_linked: boolean;
  /** Other sign-ins (devices, browsers) this change signed out. */
  other_sessions_signed_out: number;
}
export interface MembershipSummary {
  partner_id: string;
  display_name: string;
  role: PartnerRole;
}
export interface SessionTokens {
  access_token: string;
  refresh_token: string;
  expires_in_s: number;
}
export type VerifyResponse =
  | (SessionTokens & { partner_id: string; role: PartnerRole; memberships?: undefined })
  | (SessionTokens & { memberships: MembershipSummary[]; partner_id?: undefined });
export interface SelectResponse extends SessionTokens {
  partner_id: string;
  role: PartnerRole;
}
export interface Whoami {
  partner_user_id: number;
  email: string;
  partner_id: string | null;
  role: PartnerRole | null;
  /** The org has not accepted the current Partner Terms (migration 069). */
  terms_required?: boolean;
  /**
   * The server's live-conversation switch (`WIDGET_LIVE_ENABLED`). A site's
   * "Live conversation" setting takes effect only while this is on. Absent
   * from an API older than 2026-09-27.
   */
  widget_live_available?: boolean;
  /** Spec 014: whether the server's WIDGET_CHAT_ENABLED switch is on. */
  widget_chat_available?: boolean;
  /**
   * FinderlyFix staff (migration 076, PARTNER_OPERATOR_EMAILS): show the
   * "FinderlyFix admin" section. Absent from an API older than 2026-09-27.
   */
  is_operator?: boolean;
  /**
   * Migration 080: this organisation was created by an AI agent at sign-up
   * and the person it named has not claimed it yet. Null (or absent from an
   * older API) for every other organisation.
   */
  claim_pending?: ClaimState | null;
  /**
   * Contractor pages: which server switches are on (contractor billing,
   * `PARTNER_CONTRACTOR_BILLING_ENABLED`; quotes, `PARTNER_QUOTES_ENABLED`).
   * Absent from an older API.
   */
  features?: { contractor_billing: boolean; quotes: boolean };
}

/** An agent-created organisation's claim state (`GET /partner/org/claim`, whoami). */
export interface ClaimState {
  claimed: boolean;
  /** The signed-in person is the contact the agent named (only they may claim). */
  you_are_contact: boolean;
  contact_email: string | null;
  created_at: string;
  /** When the widget keys stop if nobody claims it. */
  disable_at: string;
  keys_disabled: boolean;
  keys_disabled_reason: 'unclaimed_expired' | 'contact_reported' | string | null;
  origin: string | null;
  proof_method: 'file' | 'dns' | string | null;
  agent_client: { name: string | null; version: string | null };
  attested_by: { name: string | null; role: string | null } | null;
}

export interface ClaimInfo {
  agent_created: boolean;
  claim: ClaimState | null;
  current: { terms_version: string };
}

export interface ClaimResult {
  claimed: true;
  claimed_at: string;
  keys_reenabled: boolean;
  accepted: unknown;
}

/** `POST /partner/signup/report/preview`: what the "this wasn't us" link is about. */
export interface SignupReportPreview {
  organisation: string;
  origin: string;
  created_at: string;
  reported: boolean;
  claimed: boolean;
}

export interface SignupReportResult {
  reported: true;
  already_reported: boolean;
  message: string;
}

// ── Partner Terms (`/partner/terms`) ────────────────────────────────────────
/**
 * What the portal sends to accept: the Partner Terms version only. The DPA is
 * no longer part of the click-through (it is available on request and signed
 * separately), so no DPA version is sent.
 */
export interface TermsAcceptance {
  terms_version: string;
}
export interface TermsStatus {
  current: TermsAcceptance;
  /**
   * The latest acceptance. `dpa_version` is history: the DPA version an
   * earlier click-through recorded, or null for an acceptance of Terms that
   * no longer incorporate one. `accepted_by_agent_token_id`: the AI agent
   * token that accepted an updated version for the organisation (Partner
   * Terms §2, 2026-09-27), or null when a person accepted.
   */
  accepted:
    | (TermsAcceptance & {
        dpa_version: string | null;
        accepted_at: string;
        accepted_by_user_id: number;
        accepted_by_agent_token_id?: string | null;
      })
    | null;
  terms_required: boolean;
  can_accept: boolean;
}

// ── Org ─────────────────────────────────────────────────────────────────────
export interface Org {
  id: string;
  display_name: string;
  partner_type: PartnerType;
  status: 'pending' | 'active' | 'suspended' | string;
  plan: string;
  media_retention_days: number;
  plan_flags: { powered_by_locked?: boolean; fallback_allowed?: boolean; seats?: number } & Record<string, unknown>;
}
/** `PATCH /partner/org`: the org, plus the sites a move into a neutral partner type switched the Network fallback off on (082). */
export interface OrgUpdated extends Org {
  network_fallback_turned_off?: Array<{ site_key: string; label: string }>;
}
/** An owner's request that FinderlyFix close the organisation and delete its data. */
export interface CloseRequest {
  requested_at: string;
  requested_by: string | null;
  reason: string | null;
}
/** `GET /partner/org/close-request`. */
export interface CloseRequestState {
  request: CloseRequest | null;
  /** The staff offboarding case, once FinderlyFix has opened one. */
  offboarding: { state: string; requested_at: string | null; deadline_at: string | null } | null;
  contact_email: string;
}
/** `POST /partner/org/close-request` (202). */
export interface CloseRequested {
  request: CloseRequest;
  already_requested: boolean;
}
export interface Member {
  user_id: number;
  email: string;
  name: string | null;
  role: PartnerRole;
  last_login_at: string | null;
}

// ── Sites ───────────────────────────────────────────────────────────────────
export interface SiteSummary {
  site_key: string;
  label: string;
  environment: 'live' | 'test';
  enabled: boolean;
  /** Migration 059. Absent on an older API — treat as 'any'. */
  origin_policy?: OriginPolicy;
  origins_count: number;
  last_impression_at: string | null;
  /** When the owner paused the widget on this key (and its test twin); null or absent when running. */
  paused_at?: string | null;
}
/** `POST /partner/sites/:site_key/pause`: the live key and its test twin stop serving. */
export interface SitePaused {
  site_key: string;
  paused_at: string;
  paired_site_key: string | null;
  paused_site_keys: string[];
}
/** `POST /partner/sites/:site_key/resume`. */
export interface SiteResumed {
  site_key: string;
  paused_at: null;
  resumed_site_keys: string[];
}
/**
 * 'any' (default): the key works on any https website the snippet is pasted
 * into. 'listed': only the un-blocked websites in the site's list.
 */
export type OriginPolicy = 'any' | 'listed';
/** `GET /partner/setup-progress`: rows, not clicks; setup/status.ts maps them to steps. */
export interface SetupProgress {
  /** The live key these facts are about; null when the org has no site yet. */
  site_key: string | null;
  published_version: number | null;
  /** The published config has a logo, a colour or a headline. */
  branding_customised: boolean;
  /** Whether the published config collects contact details; null when nothing is published. */
  collects_contact: boolean | null;
  last_impression_at: string | null;
  /** A page scan or headless render (tiers 1–2) found the widget. */
  install_check_passed: boolean;
  ownership_verified: boolean;
  developer_invited: boolean;
  hosted_page_served: boolean;
  active_destinations: number;
  test_lead_received: boolean;
}
export interface Site {
  site_key: string;
  label: string;
  environment: 'live' | 'test';
  paired_site_key: string | null;
  enabled: boolean;
  finderly_network_fallback: boolean;
  rotated_from: string | null;
  rotation_grace_until: string | null;
  origin_policy?: OriginPolicy;
  paused_at?: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface SiteCreated {
  live: { site_key: string; label: string; environment: 'live' };
  test: { site_key: string; label: string; environment: 'test' };
}
export interface Origin {
  id: number;
  origin: string;
  env_label: string;
  verified_at: string | null;
  last_seen_at: string | null;
  /** Set when the partner blocked this website (059). */
  blocked_at?: string | null;
  /** Set when the partner added this website (088). Null: the widget was only
   *  seen there, which does not count once the key is locked to the list. */
  listed_at?: string | null;
  created_at?: string | null;
}
export interface UnlistedAttempt {
  origin: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
  attempt_count: number;
}
/** `file_missing`: tier 3's ownership file is missing or lacks the key (092; older rows say `not_in_html`). */
export type VerificationResult = 'found' | 'not_in_html' | 'file_missing' | 'csp' | 'blocked' | 'timeout' | 'error' | 'pending';
export interface Verification {
  id: number;
  tier: 1 | 2 | 3;
  url: string;
  result: VerificationResult;
  details: Record<string, unknown> | null;
  created_at: string | null;
}
/** `POST /partner/sites/:key/csp-check` — the page's CSP against the widget's sources. */
export interface CspCheck {
  status: 'none' | 'ok' | 'missing' | 'unreachable';
  url: string;
  final_url: string | null;
  policies: Array<'header' | 'meta'>;
  missing: Array<{ directive: string; add_to: string; sources: string[] }>;
  strict_dynamic: boolean;
  reason?: string;
  message?: string;
  http_status?: number;
}
export interface VerifyStarted {
  verification_id: number;
  tier: 1 | 2 | 3;
  result: VerificationResult;
}

// ── Config ──────────────────────────────────────────────────────────────────
export interface PublishResponse {
  version: number;
  published_at: string;
  published: true;
}

// ── Destinations ────────────────────────────────────────────────────────────
export const SUBSCRIBABLE_EVENTS = [
  'lead.created',
  'lead.updated',
  'lead.accepted',
  'lead.declined',
  'lead.contacted',
  'lead.call_connected',
  'lead.consent_revoked',
  'lead.media_added',
  'diagnosis.completed',
] as const;
export type SubscribableEvent = (typeof SUBSCRIBABLE_EVENTS)[number];
export type DestinationKind = 'email' | 'sms' | 'webhook';
export interface Destination {
  id: string;
  kind: DestinationKind;
  config: { recipients?: string[]; url?: string; events?: SubscribableEvent[] } & Record<string, unknown>;
  status: 'active' | 'disabled';
  sms_campaign_status: string | null;
  secret_rotated_at: string | null;
  created_at: string | null;
}
export interface DestinationCreated extends Destination {
  /** Only on webhook creation, exactly once. */
  secret?: string;
}
export type DeliveryStatus = 'pending' | 'sent' | 'failed' | 'gave_up';
export interface Delivery {
  id: string;
  /** The lead this delivery was for; null for a destination's test event. Absent on an older API. */
  lead_id?: string | null;
  event_type: string;
  event_id: string;
  attempt: number;
  status: DeliveryStatus;
  response_code: number | null;
  created_at: string | null;
}

// ── Leads ───────────────────────────────────────────────────────────────────
export const LEAD_STATUSES = [
  'new',
  'routing',
  'delivered',
  'accepted',
  'declined',
  'unmatched',
  'connected',
  'closed',
  'revoked',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];
export const LEAD_OUTCOMES = ['diy_resolved', 'dispatched', 'reopened', 'no_action'] as const;
export const LEAD_OUTCOME_CLASSES = ['avoided_dispatch', 'deflected_job', 'dispatched', 'unknown'] as const;
export type Urgency = 'emergency' | 'urgent' | 'routine';

export interface LeadSummary {
  id: string;
  created_at: string | null;
  mode: 'live' | 'test';
  test: boolean;
  status: LeadStatus;
  outcome: string | null;
  outcome_class: string | null;
  urgency: Urgency;
  trade: string;
  /** server/src/constants/trade-labels.ts; absent from an older server. */
  trade_label?: string;
  problem_code: string | null;
  problem_label: string | null;
  unmatched_reason?: UnmatchedReason | null;
  network_fallback?: NetworkFallback | null;
  requested_action: string;
  in_scope: boolean;
  origin: string;
  site_key: string;
  contact: { first_name: unknown; city: unknown; state: unknown; postal_code: unknown } | null;
  contact_purged_at: string | null;
}
export interface LeadMedia {
  media_id: string;
  kind: string;
  url: string | null;
  expires_at: string | null;
  content_type: string;
  labels: string[];
}
export interface LeadDetail {
  schema_version: string;
  lead_id: string;
  mode: 'live' | 'test';
  test: boolean;
  partner: { partner_id: string; site_key: string; widget_config_version: number | null; origin: string; page_url: string | null };
  requested_action: string;
  urgency: Urgency;
  trade_label?: string;
  diagnosis: {
    trade?: string;
    problem_code?: string | null;
    problem_label?: string;
    summary?: string;
    confidence?: number;
    safety_escalation?: { tier?: string; reason?: string; recommended_action?: string } | null;
    cost_estimate?: { low?: number; high?: number; currency?: string };
    /** `unavailable_busy`: the widget was rate limited and could not diagnose it (#1018). */
    status?: 'unavailable_busy';
    /** A busy lead: whether the homeowner's own photo came with it. */
    photo_attached?: boolean;
  } & Record<string, unknown>;
  in_scope: boolean;
  media: LeadMedia[];
  contact: Record<string, unknown> | null;
  contact_purged_at: string | null;
  property: Record<string, unknown> | null;
  interview: unknown[];
  routing: { matched_vendors: Array<{ vendor_id: string; tier: string | null; rank: number }> };
  consent: Record<string, unknown> | null;
  attribution: Record<string, unknown>;
  status: LeadStatus;
  outcome: string | null;
  outcome_class: string | null;
  reopened_at: string | null;
  /** Why no contractor got it (migration 077); null when one did. */
  unmatched_reason?: UnmatchedReason | null;
  /** Handed to the matching network (migration 094); null when it never qualified. */
  network_fallback?: NetworkFallback | null;
  created_at: string | null;
  updated_at: string | null;
  /** Contractor billing (phase 2): contact hidden until the lead is accepted. */
  contact_locked?: boolean;
  /** The lead fee accepting this lead would add to the statement; null when none. */
  accept_fee_cents?: number | null;
  /** Accepting needs the Contractor Fee Schedule accepted first (an owner or admin, on Billing). */
  fee_schedule_required?: boolean;
  /** Contractor quotes (phase 3): the latest quote, how many, and the project. Absent from an older API. */
  quote?: LeadQuoteSummary | null;
  quotes_count?: number;
  project?: LeadProjectSummary | null;
}
/**
 * The lead's Network fallback hand-off (server migration 094): whether it was
 * passed to the matching network (eLocal) and how that went. Null when the
 * lead never qualified; absent from an older server.
 */
export interface NetworkFallback {
  status: 'queued' | 'sending' | 'accepted' | 'rejected' | 'error' | 'skipped' | 'dry_run';
  sent_at: string | null;
  reason: string | null;
  updated_at?: string | null;
}
export const UNMATCHED_REASONS = ['no_postal_code', 'no_contractors', 'no_coverage', 'outside_hours', 'at_capacity', 'none_accepted'] as const;
export type UnmatchedReason = (typeof UNMATCHED_REASONS)[number];
export type TimelineType = 'created' | 'assigned' | 'delivered' | 'accepted' | 'declined' | 'contacted' | 'closed' | 'reopened' | 'network_fallback' | 'media_added';
export interface TimelineEntry {
  ts: string;
  type: TimelineType;
  detail: Record<string, unknown>;
}

// ── Dashboard ───────────────────────────────────────────────────────────────
export interface Funnel {
  period: { from: string; to: string };
  /** `site_key` is the live key counted, or null for every site (older servers omit it). */
  filters: { origin: string | null; trade: string | null; site_key?: string | null };
  impressions: number;
  captures: number;
  diagnoses: number;
  outcomes: { diy_resolved: number; parts: number; sent_to_team: number; escalated: number };
  /** Reached your inbox or webhook (an unmatched lead's notice counts). */
  delivered: number;
  /** Offered to a contractor. Absent from an older server. */
  offered?: number;
  accepted: number;
  connected: number;
  /** No contractor matched or accepted, split by reason. Absent from an older server. */
  unmatched?: number;
  unmatched_reasons?: Partial<Record<UnmatchedReason, number>>;
  /** Unmatched leads handed to the matching network (eLocal received them). Absent from an older server. */
  sent_to_network?: number;
  /** Of those, the ones the network took. */
  network_accepted?: number;
  deflection_rate: number | null;
  false_deflection_rate: number | null;
}
export interface InstallHealthOrigin {
  origin: string;
  site_key: string | null;
  registered: boolean;
  status: 'installed' | 'unverified' | 'error';
  last_impression_at: string | null;
  script_version: string | null;
  accepted_impressions: number;
  rejected_impressions: number;
  last_rejection_reason: string | null;
  verified_at: string | null;
  last_verification: { tier: number; result: VerificationResult; created_at: string | null } | null;
}
export interface InstallHealth {
  since: string;
  origins: InstallHealthOrigin[];
}
export interface ExportJob {
  status: 'pending' | 'ready' | 'failed';
  download_url?: string;
  expires_at?: string;
  rows?: number;
  truncated?: boolean;
}

/**
 * What a share link shows, fixed by whoever created it (D-3, migration 065):
 * a rolling period ending when it is opened, optionally narrowed to one
 * origin and/or one trade. `null` = all. The viewer cannot change it.
 */
export interface ShareLinkScope {
  period_days: 7 | 30 | 90;
  origin: string | null;
  trade: string | null;
}
/** A "Share your stats" link (`/share/:token`). The token is only in the create response. */
export interface ShareLink {
  id: string;
  created_at: string;
  expires_at: string;
  last_viewed_at: string | null;
  created_by: string | null;
  /** Optional only so a portal deployed ahead of the API still renders (pre-065 servers omit it). */
  scope?: ShareLinkScope;
}
export interface ShareLinkCreateBody {
  expires_in_days?: number;
  period_days?: ShareLinkScope['period_days'];
  origin?: string;
  trade?: string;
}
export interface ShareLinkCreated extends ShareLink {
  token: string;
  url: string;
}
/** `GET /share/:token` — public, no session. Aggregate counts only. */
export type SharedDashboard =
  | { valid: true; partner: { display_name: string }; expires_at: string; scope?: ShareLinkScope; funnel: Omit<Funnel, 'filters'> }
  | { valid: false };

// ── Hosted page (finderlyfix.com/p/{slug}) ──────────────────────────────────
export interface HostedPage {
  slug: string;
  url: string;
  site_key: string;
  headline: string | null;
  intro: string | null;
  enabled: boolean;
  /**
   * Whether finderlyfix.com/p/{slug} is actually serving it now: enabled, on
   * a live enabled key, for a partner that is not suspended. `enabled` alone
   * can be true while the public page is a 404.
   */
  served: boolean;
  /** Whether search engines may index it: served, the organisation approved, and a widget config published. */
  indexable?: boolean;
  created_at: string;
  updated_at: string;
}

/** `GET /partner/hosted-pages`: the organisation's pages, oldest first, and how many it may have. */
export interface HostedPageList {
  items: HostedPage[];
  /** MAX_HOSTED_PAGES_PER_ORG on the server (3). */
  limit: number;
  used: number;
}
export interface HostedPageCreateBody {
  slug?: string;
  site_key?: string;
  headline?: string;
  intro?: string;
}
export interface HostedPagePatch {
  headline?: string;
  intro?: string;
  enabled?: boolean;
  site_key?: string;
}

// ── Audit ───────────────────────────────────────────────────────────────────
export interface AuditEntry {
  id: string;
  action: string;
  resource: string;
  actor_user_id: number | null;
  actor_operator: string | null;
  /** Migration 070: set when an AI agent did it with a token `actor_user_id` created. */
  actor_agent_token_id?: string | null;
  actor_agent_token_label?: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  created_at: string;
}

// ── Agent access (migration 070) ────────────────────────────────────────────
export type AgentScope = 'read' | 'config';

export interface AgentToken {
  id: string;
  label: string;
  /** `fxa_abc123…` — enough to tell tokens apart, not to use one. `null` for a browser sign-in (OAuth). */
  token_prefix: string | null;
  /** `oauth`: an MCP client signed in through the browser (T097); its tokens renew themselves. Absent on older servers. */
  kind?: 'header' | 'oauth';
  /** The OAuth client, by its metadata URL and the name it gave. */
  client?: { id: string | null; name: string | null } | null;
  scopes: AgentScope[];
  site_keys: string[] | null;
  created_by: number;
  created_by_email: string | null;
  created_at: string;
  expires_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** Migration 083: the token an agent renewed into this one. Absent on older servers. */
  renewed_from?: string | null;
  /** Migration 083: when an agent renewed this token; it then works only for a short grace period. */
  superseded_at?: string | null;
  status: 'active' | 'expired' | 'revoked';
}

/** Migration 083: a team invitation an AI agent asked for; the invitee accepts it from an emailed link. */
export type MemberInviteStatus = 'open' | 'accepted' | 'revoked' | 'expired';

export interface MemberInvite {
  id: string;
  email: string;
  role: Exclude<PartnerRole, 'owner'>;
  status: MemberInviteStatus;
  invited_by: string | null;
  invited_via_agent: true;
  agent_label: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

/** `POST /partner/invites/preview` (public: the link's token is the authority). */
export interface MemberInvitePreview {
  organisation: string;
  email: string;
  role: Exclude<PartnerRole, 'owner'>;
  status: MemberInviteStatus;
  expires_at: string;
  invited_by: string | null;
  invited_via_agent: true;
  agent_label: string | null;
}

export interface MemberInviteAccepted {
  organisation: string;
  email: string;
  role: Exclude<PartnerRole, 'owner'>;
  already_member: boolean;
  sign_in_url: string;
}

/** The create response: the only time `token` exists outside the agent's config. */
export interface AgentTokenCreated extends AgentToken {
  token: string;
}

/** `GET /partner/agent-setup-bundle` (T098): agent config a team may commit. No secret inside. */
export interface AgentSetupBundleFile {
  path: string;
  content: string;
  /** What to do when the repository already has the file. */
  merge: 'replace' | 'append-section' | 'merge-json';
}

export interface AgentSetupBundle {
  env_var: string;
  files: AgentSetupBundleFile[];
}

export interface AgentTokenCreateBody {
  label: string;
  scopes: AgentScope[];
  site_keys?: string[] | null;
  expires_in_days?: number;
}

/** The consent screen's view of an MCP OAuth sign-in request (T097). */
export interface OAuthRequestDetails {
  /** `name` is what the app says it is; `host` is where that claim is published. */
  client: { id: string; name: string; host: string };
  /** Where the browser goes after the decision; `loopback` = an app on this computer. */
  redirect: { uri: string; host: string; loopback: boolean };
  /** What the app asked for; the person may narrow it, not widen it. */
  scopes: AgentScope[];
  resource: string;
  expires_at: string;
  max_days: number;
  default_days: number;
}

export interface OAuthApproveBody {
  scopes: AgentScope[];
  site_keys?: string[] | null;
  expires_in_days?: number;
}

/**
 * 096: `applied` — an agent's change, live (undoable until `undo.until`);
 * `reverted` — a person undid it; `pending` — a held change waiting for an
 * owner or admin (`needs_approval`); `approved` — a person applied it.
 */
export type PendingChangeStatus = 'pending' | 'approved' | 'applied' | 'rejected' | 'expired' | 'failed' | 'reverted';

/** Migration 074: what an agent may ask for, taking effect after a window. */
export type PendingChangeKind =
  | 'destination.create'
  | 'destination.update'
  | 'vendor.activate'
  | 'routing.escalation'
  | 'site.network_fallback'
  | 'member.invite'
  | 'vendor.reconsent'
  // Migration 086: an agent's publish that moves a homeowner's number or link.
  | 'config.contact_targets';

export interface PendingChange {
  id: string;
  kind: PendingChangeKind;
  /** destination.create: { kind, config }; destination.update: { destination_id, config }; vendor.activate: { vendor_ids, … }; routing.escalation: { emails }; site.network_fallback: { site_key, enabled: true }; member.invite: { email, role } (migration 083, never owner); config.contact_targets: { site_key, version, from_version, changes: [{ field, from, to }] } (migration 086). */
  payload: { kind?: DestinationKind; config?: Record<string, unknown> } & Record<string, unknown>;
  summary: string;
  /** The agent's own words. Render as a quote, never as our copy. */
  note: string | null;
  status: PendingChangeStatus;
  requested_by: { agent_token_id: string | null; agent_token_label: string | null; user_id: number; email: string | null };
  decided_by_user_id: number | null;
  decided_at: string | null;
  result: Record<string, unknown> | null;
  /** 096: true while it waits for an owner or admin (a held change). Older servers omit it. */
  needs_approval?: boolean;
  /** 096: before → after, as the owners' email said it. */
  changes?: Array<{ field: string; before: string; after: string; note?: string }>;
  /** 096: one-click Undo of an applied agent change. */
  undo?: { available: boolean; until: string | null; how: string | null };
  reverted_at?: string | null;
  reverted_by_user_id?: number | null;
  /** 096: a person chose "Keep it"; the dashboard banner no longer shows it. */
  reviewed_at?: string | null;
  /** 074's window: when an older change takes effect on its own. Null for a held change (it waits for a person). */
  applies_at: string | null;
  expires_at: string;
  created_at: string;
}

/** 096: what an emailed Undo / Approve / Reject link opens (no session). */
export interface AgentChangeLinkPreview {
  action: 'undo' | 'approve' | 'reject';
  partner_name: string | null;
  signed_in_as: string;
  can_act: boolean;
  change: PendingChange;
}

export interface PendingChangeApproved {
  change: PendingChange;
  /** destination.create and destination.update only. */
  destination?: Destination;
  /** A webhook's signing secret, shown once to the person who approved. */
  secret?: string;
}

// ── Lead brief (public) ─────────────────────────────────────────────────────
export type BriefResponse =
  | {
      valid: true;
      lead: {
        trade: string;
        problem_label: string | null;
        summary: string | null;
        /** `unavailable_busy`: no diagnosis, the widget was busy (#1018). Absent from an older server. */
        diagnosis_status?: 'unavailable_busy' | null;
        cost_estimate: { low?: number; high?: number; currency?: string } | null;
        urgency: Urgency;
        contact: {
          first_name?: string | null;
          phone_1?: string | null;
          address_1?: string | null;
          city?: string | null;
          state?: string | null;
          postal_code?: string | null;
        };
        media: Array<{ url: string; kind: string }>;
      };
      sla_expires_at: string | null;
      /** #869: ticked before Accept / "I called"; the version goes back on the POST. */
      contact_rules?: BriefContactRules;
    }
  | { valid: false };
export interface BriefContactRules {
  version: string;
  partner_name: string;
  rules: string[];
  acknowledgement: string;
}
export type BriefContact = {
  first_name?: string | null;
  phone_1?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
};
export type BriefActionResponse =
  | { ok: true; status: 'accepted' | 'declined' | 'contacted'; contact?: BriefContact }
  | { ok: false; reason: 'expired' | 'used' | 'not_found' | 'rules_not_acknowledged' };

// ── Vendors (the contractor roster, routes/partner/vendors.ts) ──────────────
export const VENDOR_TIERS = ['preferred', 'standard', 'overflow'] as const;
export type VendorTier = (typeof VENDOR_TIERS)[number];

export interface Vendor {
  id: string;
  external_id: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  trades: string[];
  postal_prefixes: string[];
  tier: VendorTier | string;
  hours: unknown;
  /** Stored hours as text ("Mon-Fri 08:00-18:00"); null = always. */
  hours_text: string | null;
  timezone: string | null;
  capacity_per_day: number | null;
  active: boolean;
  do_not_contact: boolean;
  /** Service-area centre + radius (066): all three set, or all null. */
  lat: number | null;
  lng: number | null;
  radius_miles: number | null;
  license: VendorLicense[];
  /** `YYYY-MM-DD` or null. */
  insurance_expires_at: string | null;
  /**
   * An AI agent added it or changed its phone/email (migration 070): it is
   * inactive and receives nothing until an owner or admin activates it,
   * confirming the contractor contact rules.
   */
  pending_activation?: boolean;
  pending_activation_token_id?: string | null;
  created_at: string;
  updated_at: string;
}

// ── Contractor opt-outs (migration 084) ─────────────────────────────────────

/** How a contractor can tell the partner they want offers again (server `ATTESTED_RECONSENT_CHANNELS`). */
export const RECONSENT_CHANNELS = [
  { value: 'phone', label: 'On the phone', phrase: 'by phone' },
  { value: 'email_reply', label: 'By email', phrase: 'by email' },
  { value: 'in_person', label: 'In person', phrase: 'in person' },
  { value: 'form', label: 'A written form they filled in', phrase: 'in a written form' },
] as const;
export type ReconsentChannel = (typeof RECONSENT_CHANNELS)[number]['value'];

/**
 * The sentence a person confirms, word for word the server's
 * (services/vendor-consent.ts `reconsentAttestation`): always the
 * CONTRACTOR's statement, never consent given for them.
 */
export function reconsentAttestation(contractorName: string, receivedAt: Date, channel: ReconsentChannel): string {
  const date = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(receivedAt);
  const phrase = RECONSENT_CHANNELS.find((c) => c.value === channel)?.phrase ?? channel;
  return `${contractorName} told us on ${date} ${phrase} that they want offers again.`;
}

export interface VendorReconsentInput {
  channel: ReconsentChannel;
  /** ISO 8601 with offset. */
  received_at: string;
  evidence: string;
  attested: true;
}

export interface VendorConsentEvent {
  id: string;
  vendor_id: string;
  vendor_name: string | null;
  event: 'opted_out' | 're_consented';
  channel: string;
  received_at: string;
  effective_at: string;
  evidence: string | null;
  attestation: string | null;
  actor: { kind: 'contractor' | 'person' | 'agent' | 'system'; user_id: number | null; agent_token_id: string | null; label: string | null };
  pending_change_id: string | null;
  created_at: string;
}

export interface VendorLicense {
  state: string;
  number: string;
  trade?: string;
  expires_at?: string;
}

export interface VendorBulkResult {
  updated: number;
  unchanged: number;
  not_found: number;
  items: Array<{ id: string; result: 'updated' | 'unchanged' | 'not_found' }>;
}

/** POST body; PATCH takes any subset. */
export interface VendorInput {
  name: string;
  external_id?: string | null;
  phone?: string | null;
  email?: string | null;
  trades: string[];
  postal_prefixes: string[];
  tier?: VendorTier;
  hours?: string | null;
  timezone?: string | null;
  capacity_per_day?: number | null;
  active?: boolean;
  do_not_contact?: boolean;
  lat?: number | null;
  lng?: number | null;
  radius_miles?: number | null;
  /** Replaces the whole list. */
  license?: VendorLicense[] | null;
  insurance_expires_at?: string | null;
  /** Needed to activate a contractor an AI agent prepared. */
  contact_rules_attested?: true;
}

/** What the organisation confirms when activating a contractor an AI agent prepared (server: CONTACT_RULES_ATTESTATION). */
export const CONTACT_RULES_ATTESTATION =
  'My organisation has bound these contractors, in writing, to the FinderlyFix contractor contact rules (Partner Terms §3).';

export interface VendorPreview {
  external_id: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  trades: string[];
  postal_prefixes: string[];
  tier: string;
  hours_text: string | null;
  timezone: string | null;
  capacity_per_day: number | null;
  active: boolean;
  lat?: number | null;
  lng?: number | null;
  radius_miles?: number | null;
  license?: VendorLicense[];
  insurance_expires_at?: string | null;
}

export interface VendorImportRow {
  row: number;
  action: 'create' | 'update' | 'error';
  vendor_id: string | null;
  name: string;
  vendor?: VendorPreview;
}

// ── Routing (the accept-deadline cascade, routes/partner/routing.ts) ─────────
export const LEAD_URGENCIES = ['emergency', 'urgent', 'routine'] as const;
export type LeadUrgency = (typeof LEAD_URGENCIES)[number];
/** The two matching strategies route-lead implements. */
export type RoutingStrategy = 'preferred_tier_first' | 'round_robin';

export interface RoutingRuleInput {
  match: { trade?: string[]; urgency?: LeadUrgency[]; postal_prefix?: string[] };
  action: { vendor_ids: string[] };
}

export interface RoutingRule extends RoutingRuleInput {
  id: number;
  position: number;
}

export interface RoutingSettings {
  strategy: RoutingStrategy | string;
  accept_sla_minutes: number;
  urgency_sla_minutes: Partial<Record<LeadUrgency, number>>;
  cascade_depth: number;
  escalation: { emails: string[] };
  urgency_rules: Array<{ urgency: LeadUrgency; description: string }>;
  /** false: never saved; these are the defaults. */
  configured: boolean;
  updated_at: string | null;
}

export interface RoutingResponse extends RoutingSettings {
  rules: RoutingRule[];
  defaults: { strategy: RoutingStrategy; accept_sla_minutes: number; cascade_depth: number };
  /** A proposal awaiting FinderlyFix sign-off; never applied unless the partner saves it. */
  suggested_urgency_sla_minutes: Record<LeadUrgency, number>;
  limits: {
    sla_minutes: { min: number; max: number };
    cascade_depth: { min: number; max: number };
    escalation_emails: number;
    rules: number;
  };
}

export interface RoutingPatch {
  strategy?: RoutingStrategy;
  accept_sla_minutes?: number;
  urgency_sla_minutes?: Partial<Record<LeadUrgency, number | null>>;
  cascade_depth?: number;
  escalation?: { emails: string[] };
  urgency_rules?: Array<{ urgency: LeadUrgency; description: string }>;
}

export interface VendorImportResult {
  import_id: string;
  dry_run: boolean;
  accepted: number;
  rejected: number;
  created: number;
  updated: number;
  errors: Array<{ row: number; field: string; message: string }>;
  rows: VendorImportRow[];
}

// ── Network fallback (migration 082) ──────────────────────────────────────
export type FallbackNetwork = 'elocal' | 'neutral';
/** self_serve: eLocal types switch it themselves. review: `other` asks, an operator checks. unavailable: neutral types, no network yet. */
export type FallbackAccess = 'self_serve' | 'review' | 'unavailable';
export interface FallbackRequest {
  id: string;
  kind: 'enable' | 'notify';
  site_key: string | null;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  /** The partner's own words. */
  note: string | null;
  requested_by_email: string | null;
  requested_by_agent: boolean;
  /** An operator's reason (declined, or approved with a note), or the system's (partner type change). */
  decision_reason: string | null;
  decided_at: string | null;
  created_at: string | null;
}
/** `GET /partner/sites/:site_key/network-fallback`. */
export interface NetworkFallbackState {
  /** The pair's live key; the switch writes it and `test_site_key`. */
  site_key: string;
  test_site_key: string | null;
  label: string;
  enabled: boolean;
  partner_type: string;
  network: FallbackNetwork;
  access: FallbackAccess;
  /** decisions/fallback-network.md §4, verbatim. */
  disclosure: string;
  /** The newest "please turn it on" request for this site, any status. */
  request: FallbackRequest | null;
  notify_requested: boolean;
  /** An AI agent's "turn it on" still inside its window. */
  pending_agent_change: { id: string; applies_at: string | null } | null;
}
export interface OpsFallbackRequest extends FallbackRequest {
  partner_id: string;
  partner_display_name: string;
  partner_type: string;
  network: FallbackNetwork;
  access: FallbackAccess;
  site_label: string | null;
  site_enabled: boolean | null;
  /** `portal:<id>:<email>` or `system`. */
  decided_by: string | null;
}
export interface OpsNetworkFallback {
  network: FallbackNetwork;
  access: FallbackAccess;
  disclosure: string;
  sites: Array<{ site_key: string; test_site_key: string | null; label: string; enabled: boolean }>;
  requests: OpsFallbackRequest[];
}

// ── Widget usage limits (migration 089) ──────────────────────────────────
export type WidgetLimitKind = 'lead_hour' | 'lead_day' | 'ai_hour' | 'live_hour' | 'chat_hour';
export type WidgetLimitValues = Partial<Record<WidgetLimitKind, number>>;
export interface WidgetLimitUsage {
  kind: WidgetLimitKind;
  label: string;
  /** Used in the current window; null when the counters could not be read. */
  used: number | null;
  limit: number;
  default_limit: number;
  /** FinderlyFix raised (or set) this limit for the site. */
  overridden: boolean;
  window_seconds: number;
  resets_at: string;
}
export interface WidgetLimitRequest {
  id: string;
  site_key: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  requested: WidgetLimitValues;
  granted: WidgetLimitValues | null;
  note: string | null;
  requested_by_email: string | null;
  requested_by_agent: boolean;
  decision_reason: string | null;
  decided_at: string | null;
  created_at: string | null;
}
export interface WidgetUsageLimits {
  site_key: string;
  label: string;
  limits: WidgetLimitUsage[];
  pending_request: WidgetLimitRequest | null;
  last_decision: WidgetLimitRequest | null;
}
export interface OpsWidgetLimitRequest extends WidgetLimitRequest {
  partner_id: string;
  partner_display_name: string;
  site_label: string | null;
  decided_by: string | null;
  current: WidgetLimitUsage[];
}

// ── FinderlyFix operator console (`/partner/ops/*`, migration 076) ─────────
export type PartnerLifecycle = 'pending' | 'active' | 'disabled' | 'offboarding';

export interface OpsPartnerSite {
  site_key: string;
  label: string;
  environment: 'live' | 'test';
  enabled: boolean;
  origins: string[];
  last_impression_at: string | null;
}

/** One organisation in the operator overview. */
export interface OpsPartner {
  id: string;
  display_name: string;
  partner_type: string;
  status: string;
  lifecycle: PartnerLifecycle;
  plan: string;
  created_at: string | null;
  offboarded_at: string | null;
  /** What the operator types to confirm disabling. */
  confirm_slug: string;
  owner_emails: string[];
  members: number;
  sites: OpsPartnerSite[];
  widget: { live: boolean; impressions_7d: number; last_impression_at: string | null };
  terms: { terms_version: string; dpa_version: string; accepted_at: string | null; accepted_by: string | null } | null;
  agent: { active_tokens: number; last_used_at: string | null; pending_changes: number };
  leads_7d: number;
  last_activity_at: string | null;
  /** Migration 080: how an AI agent signed this organisation up; null when a person created it. */
  agent_signup?: OpsAgentSignup | null;
  /** Migration 080: the organisation's widget keys are switched off, and why. */
  keys_disabled?: { at: string | null; reason: 'unclaimed_expired' | 'contact_reported' | string | null } | null;
  /** Whether staff may free this organisation's website for a new sign-up, and whether they did. Absent on an older API. */
  site_release?: OpsSiteRelease;
}

export interface OpsSiteRelease {
  /** The organisation is disabled, suspended, reported or offboarded. */
  eligible: boolean;
  released_at: string | null;
  released_by: string | null;
  host: string | null;
  verified_origins: string[];
}

export interface OpsAgentSignup {
  host: string;
  origin: string | null;
  proof_method: 'file' | 'dns' | string | null;
  agent_client: { name: string | null; version: string | null };
  contact_email: string | null;
  claimed: boolean;
  claimed_at: string | null;
  disable_at: string | null;
  reported_at: string | null;
}

export type OpsCounts = Record<PartnerLifecycle | 'all', number>;

/** `GET /partner/ops/partners/:id`: the bearer admin detail plus the overview row. */
export interface OpsPartnerDetail {
  id: string;
  display_name: string;
  partner_type: string;
  status: string;
  plan: string;
  plan_flags: Record<string, unknown>;
  media_retention_days: number;
  created_at: string | null;
  updated_at: string | null;
  counts: { sites: number; vendors: number; destinations: number; leads: number; leads_7d: number; members: number };
  overview: OpsPartner;
  /** Migration 082: the switch per site and the recent requests. */
  network_fallback?: OpsNetworkFallback;
}

/** The partner row approve/disable answer with. */
export interface OpsPartnerState {
  id: string;
  display_name: string;
  status: string;
}

// ── Contractor quotes and projects (phase 3) ──────────────────────────────
// Mirrors server/src/services/lead-projects.ts.

export type LeadQuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'withdrawn';

export type LeadProjectStatus =
  | 'accepted'
  | 'in_progress'
  | 'completed_reported'
  | 'completed_confirmed'
  | 'disputed'
  | 'cancelled';

export interface LeadQuoteLineItem {
  description: string;
  amount_cents: number;
}

/** The latest quote on a lead (`GET /partner/leads/:id` `quote`). */
export interface LeadQuoteSummary {
  id: string;
  version: number;
  status: LeadQuoteStatus;
  amount_cents: number;
  currency: 'USD';
  scope: string;
  line_items: LeadQuoteLineItem[] | null;
  terms: string | null;
  valid_until: string | null;
  /** Snapshotted when the quote is sent; null on a draft. */
  success_fee_bps: number | null;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  withdrawn_at: string | null;
  created_at: string;
  updated_at: string;
}

/** The project an accepted quote opened (`GET /partner/leads/:id` `project`). */
export interface LeadProjectSummary {
  id: string;
  quote_id: string;
  status: LeadProjectStatus;
  contract_value_cents: number;
  success_fee_bps: number;
  started_at: string | null;
  reported_at: string | null;
  completion_note: string | null;
  auto_confirm_at: string | null;
  confirmed_at: string | null;
  confirmed_by: 'homeowner' | 'auto' | 'operator' | null;
  disputed_at: string | null;
  dispute_reason: string | null;
  resolved_at: string | null;
  resolution: 'confirmed' | 'cancelled' | 'adjusted' | null;
  resolution_note: string | null;
  final_value_cents: number | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
}

// WS-A2 additions below
// ── Hosted page profile (contractor pages phase 1, migration 100) ───────────
/** `HH:MM` 24-hour opening and closing time. */
export type HostedPageHoursRange = [string, string];
export const HOSTED_PAGE_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type HostedPageWeekday = (typeof HOSTED_PAGE_WEEKDAYS)[number];
export type HostedPageHours = Record<HostedPageWeekday, HostedPageHoursRange[]> & { emergency_24_7: boolean };
export interface HostedPageService {
  /** ≤ 80 */
  name: string;
  /** ≤ 240 */
  description: string | null;
}
export interface HostedPageLicense {
  /** ≤ 40 */
  number: string;
  /** Two letters (a US state), or null. */
  state: string | null;
  /** ≤ 80, e.g. "Washington L&I". */
  authority: string | null;
}
export interface HostedPageTestimonial {
  /** ≤ 60 */
  author: string;
  /** ≤ 400 */
  text: string;
  rating: 1 | 2 | 3 | 4 | 5 | null;
  /** `YYYY-MM-DD` */
  date: string | null;
}
export interface HostedPageFaq {
  /** ≤ 160 */
  q: string;
  /** ≤ 600 */
  a: string;
}
export interface HostedPageCta {
  primary_label: string | null;
  quote_label: string | null;
  quote_enabled: boolean;
  quote_intro: string | null;
}
/** `PUT /partner/hosted-pages/:slug/profile` body: every key optional, arrays replace. */
export interface HostedPageProfileInput {
  /** E.164, e.g. +12065550100. */
  phone: string | null;
  show_call_button: boolean;
  email: string | null;
  service_area_summary: string | null;
  /** ≤ 100 ZIP codes or prefixes. */
  service_area_postal_codes: string[];
  /** Codes from `trade_options`. */
  trades: string[];
  services: HostedPageService[];
  hours: HostedPageHours | null;
  hours_note: string | null;
  license: HostedPageLicense | null;
  insured: boolean;
  insurance_note: string | null;
  founded_year: number | null;
  about: string | null;
  testimonials: HostedPageTestimonial[];
  faq: HostedPageFaq[];
  cta: HostedPageCta;
  logo_asset_id: string | null;
  /**
   * The server's nested spelling of the two service-area keys (WS-A1's
   * schema reads and writes `service_area: { summary, postal_codes }`). The
   * portal reads either and writes back the spelling the GET used
   * (hostedPage/model.ts `serviceAreaWire`).
   */
  service_area?: { summary: string | null; postal_codes: string[] };
  /** Read-only: "(206) 555-0100", derived from `phone`. Never sent. */
  phone_display?: string | null;
  /** Read-only. Never sent. */
  updated_at?: string | null;
}
export type HostedPageAssetKind = 'logo' | 'gallery' | 'og';
/** A photo or logo uploaded for the page (served by the API, immutable by id). */
export interface HostedPageAsset {
  id: string;
  kind: HostedPageAssetKind;
  url: string;
  content_type: string;
  width: number;
  height: number;
  bytes: number;
  alt: string | null;
  caption: string | null;
  sort_order: number;
  created_at: string;
}
/** `testimonials` is the server's spelling of `testimonial`; both are understood. */
export type HostedPageCompletenessKey = 'phone' | 'photos' | 'services' | 'about' | 'hours' | 'license' | 'testimonial' | 'testimonials';
/** `GET`/`PUT /partner/hosted-pages/:slug/profile`. */
export interface HostedPageProfileResponse {
  page: Pick<HostedPage, 'slug' | 'url' | 'enabled' | 'served'>;
  profile: HostedPageProfileInput;
  assets: HostedPageAsset[];
  completeness: { score: number; missing: HostedPageCompletenessKey[] };
  trade_options: Array<{ code: string; label: string }>;
  /** Whether the page's "Request a quote" form can be switched on, and if not, why. */
  quote_form: { enabled: boolean; reason: string | null };
}
export interface HostedPageAssetUpload {
  file: Blob;
  kind: HostedPageAssetKind;
  alt?: string;
  caption?: string;
}
export interface HostedPageAssetPatch {
  alt?: string | null;
  caption?: string | null;
  sort_order?: number;
}
// WS-B2 additions below
// ── Contractor billing (phase 2) ──────────────────────────────────────────
// Mirrors server/src/routes/partner/billing.ts and ops-billing.ts.

export type BillingModel = 'none' | 'contractor_ppl';
export type LedgerKind = 'lead_fee' | 'success_fee' | 'credit' | 'adjustment' | 'payment';
export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'void';

/** The organisation's billing account (`partner_billing_accounts`). */
export interface BillingAccountView {
  partner_id: string;
  billing_model: BillingModel;
  currency: 'USD';
  /** The price of one accepted lead, unless `lead_fee_by_trade` names the lead's trade. */
  lead_fee_cents: number;
  lead_fee_by_trade: Record<string, number>;
  success_fee_bps: number;
  fees_enabled: boolean;
  /** Leads created before this never bill. */
  enrolled_at: string | null;
  billing_email: string | null;
  legal_name: string | null;
  billing_address: Record<string, unknown> | null;
  fee_schedule_version: string | null;
  fee_schedule_accepted_at: string | null;
}

/** Where the organisation stands on the Contractor Fee Schedule. */
export interface BillingFeeScheduleState {
  current_version: string;
  path: string;
  accepted_version: string | null;
  /** An owner or admin has to accept the current version before new fees post. */
  required: boolean;
  lead_fee_cents: number;
  success_fee_bps: number;
}

/** `GET /partner/billing` (and the fee-schedule accept answer). */
export interface BillingOverview {
  enabled: boolean;
  account: BillingAccountView | null;
  /** Positive: the organisation owes FinderlyFix. Negative: a credit. */
  balance_cents: number;
  open_invoice_count: number;
  fee_schedule: BillingFeeScheduleState;
}

/** `PATCH /partner/billing`: the billing contact. */
export interface BillingContactPatch {
  billing_email?: string | null;
  legal_name?: string | null;
  billing_address?: Record<string, unknown> | null;
}

/** One statement line (`billing_ledger_entries`). */
export interface LedgerEntryView {
  id: string;
  kind: LedgerKind;
  /** Positive: owed. Negative: a credit or a payment. */
  amount_cents: number;
  currency: string;
  description: string;
  lead_id: string | null;
  quote_id: string | null;
  project_id: string | null;
  invoice_id: string | null;
  posted_at: string;
}

/** The statement's filters (`GET /partner/billing/ledger` and `ledger.csv`). */
export interface LedgerFilters {
  kind?: LedgerKind;
  /** YYYY-MM-DD, inclusive. */
  from?: string;
  to?: string;
}

export interface InvoiceView {
  id: string;
  /** FX-YYYY-NNNNNN, given when the invoice is issued; null on a draft. */
  number: string | null;
  period_start: string;
  period_end: string;
  status: InvoiceStatus;
  total_cents: number;
  currency: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
}

/** `GET /partner/billing/invoices/:id`: the invoice's own fields plus its lines, flat. */
export interface InvoiceDetail extends InvoiceView {
  entries: LedgerEntryView[];
}

/** One organisation in `GET /partner/ops/billing/summary`. */
export interface OpsBillingSummaryRow {
  partner_id: string;
  display_name: string;
  balance_cents: number;
  uninvoiced_cents: number;
  open_disputes: number;
  fees_enabled: boolean;
  lead_fee_cents: number;
}

/** `GET /partner/ops/partners/:id/billing`. */
export interface OpsPartnerBilling {
  account: BillingAccountView | null;
  balance_cents: number;
  /** The last 50 entries, newest first. */
  entries: LedgerEntryView[];
  invoices: InvoiceView[];
}

/** `PUT /partner/ops/partners/:id/billing` (the portal adds `confirm`). */
export interface OpsBillingAccountUpdate {
  billing_model?: BillingModel;
  lead_fee_cents?: number;
  lead_fee_by_trade?: Record<string, number>;
  success_fee_bps?: number;
  fees_enabled?: boolean;
  note: string;
}

/** `POST /partner/ops/partners/:id/billing/entries` (the portal adds `confirm`). */
export interface OpsLedgerEntryInput {
  kind: 'credit' | 'adjustment' | 'payment';
  amount_cents: number;
  description: string;
  lead_id?: string;
  project_id?: string;
  /** The entry this one reverses. */
  reverses?: string;
}

export type OpsInvoiceAction = 'issue' | 'paid' | 'void';
// WS-C2 additions below

/** Why "Create a quote" is unavailable (`GET /partner/leads/:id/quotes` `blocked_reason`). */
export type QuoteBlockedReason =
  | 'QUOTES_DISABLED'
  | 'BILLING_NOT_ENABLED'
  | 'FEE_SCHEDULE_REQUIRED'
  | 'LEAD_NOT_ACCEPTED'
  | 'CONTACT_EMAIL_MISSING'
  | 'QUOTE_OPEN_EXISTS'
  | 'PROJECT_EXISTS';

/** The other 409 codes a quote or project write can answer with. */
export type QuoteConflictCode =
  | 'QUOTE_NOT_DRAFT'
  | 'QUOTE_NOT_OPEN'
  | 'LINE_ITEMS_MISMATCH'
  | 'PROJECT_NOT_STARTABLE'
  | 'PROJECT_NOT_COMPLETABLE'
  | 'PROJECT_NOT_CANCELLABLE';

/**
 * A quote line as the API takes it (`schemas/lead-quotes.ts` lineItemSchema,
 * strict): description ≤ 200 and `amount_cents`; the lines sum to the quote's
 * `amount_cents`. The composer's quantity × unit price is folded into the
 * description ("Labour (2.5 × $120)") because the API stores no quantity.
 */
export type QuoteLineItemInput = LeadQuoteLineItem;

/**
 * A stored line as the portal reads it: the shipped `{ description,
 * amount_cents }`, tolerating `qty` / `unit_cents` / `total_cents` should a
 * later API add them. Readers go through `lineItemTotalCents` (leads/feeMath.ts).
 */
export type QuoteLineItemView = { description: string; amount_cents?: number; qty?: number; unit_cents?: number; total_cents?: number };

/** `GET /partner/leads/:id/quotes`. */
export interface LeadQuotes {
  items: LeadQuoteSummary[];
  project: LeadProjectSummary | null;
  can_quote: boolean;
  blocked_reason: QuoteBlockedReason | null;
  /** The success fee a quote sent now would snapshot (3000 = 30%). */
  fee_bps: number;
}

/** `POST /partner/leads/:id/quotes`. `valid_days` 1–90 (server default 14). */
export interface QuoteCreateBody {
  amount_cents: number;
  scope: string;
  /** null (PATCH) clears the lines; an empty array is refused. */
  line_items?: QuoteLineItemInput[] | null;
  /** null (PATCH) clears the terms. */
  terms?: string | null;
  valid_days?: number;
  /** true sends it to the homeowner at once; absent saves a draft. */
  send?: boolean;
}

/** `PATCH /partner/quotes/:id` (draft only). */
export type QuotePatch = Partial<Omit<QuoteCreateBody, 'send'>>;

export interface QuoteFeePreview {
  success_fee_cents: number;
  net_cents: number;
  bps: number;
}

export interface QuoteCreated {
  quote: LeadQuoteSummary;
  fee_preview: QuoteFeePreview;
}

export interface ProjectLedgerEntry {
  id: string;
  kind: string;
  amount_cents: number;
  posted_at: string;
}

/** `GET /partner/projects/:id`. */
export interface ProjectDetail {
  project: LeadProjectSummary;
  quote: LeadQuoteSummary | null;
  ledger: ProjectLedgerEntry[];
}

// Public homeowner quote links (`/q/:token`): no auth, no homeowner PII.

export type PublicQuoteFailReason = 'not_found' | 'expired' | 'used';

export interface PublicQuoteContractor {
  name: string;
  phone: string | null;
  logo_url: string | null;
}

export interface PublicQuote {
  id: string;
  version: number;
  amount_cents: number;
  currency: string;
  scope: string;
  line_items: QuoteLineItemView[] | null;
  terms: string | null;
  valid_until: string | null;
  status: LeadQuoteStatus;
  sent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
}

export interface PublicQuoteProject {
  id: string;
  status: LeadProjectStatus;
  contract_value_cents: number;
  reported_at: string | null;
  auto_confirm_at: string | null;
  confirmed_at: string | null;
  confirmed_by: 'homeowner' | 'auto' | 'operator' | null;
  disputed_at: string | null;
  completion_note: string | null;
}

/** `GET /q/:token`. */
export type PublicQuoteResponse =
  | {
      ok: true;
      valid: true;
      kind: 'quote' | 'completion';
      contractor: PublicQuoteContractor;
      quote: PublicQuote;
      project: PublicQuoteProject | null;
    }
  | { ok: false; reason: PublicQuoteFailReason };

/** `POST /q/:token/accept|decline|confirm|dispute`. */
export type PublicQuoteActionResponse =
  | { ok: true; status: 'accepted' | 'declined' | 'completed_confirmed' | 'disputed'; project?: PublicQuoteProject | null }
  | { ok: false; reason: PublicQuoteFailReason };
